import { FINANCIAL_SERVER_URL } from "@pma/utils/api-routes";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { parseCookies } from "nookies";
import { ChevronUp } from "lucide-react";
import { Button } from "@components/common/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card";
import PmsLoader from "@components/common/loader";
import QuoteRow from "../financials/quote-row";
import useTranslator from "@pma/hooks/use-translator";
import { DictType } from "@pma/types";
import { useState } from "react";

interface Props {
  projectId: string;
}
const ProjectQuoteManagerTab = ({ projectId }: Props) => {
  const [expandedCards, setExpandedCards] = useState<{ [key: number]: boolean }>({
    0: false,
  });
  const { translate } = useTranslator();

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const toggleCard = (index: number) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };
  const getProjectQuotes = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + "#",
      {},
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["project-quotes", projectId],
    queryFn: getProjectQuotes,
    enabled: false,
  });

  const dummyQuotes = [
    {
      id: "#456",
      title: "Cloud Migration",
      hours: 60,
      totalHours: 100,
      cost: 4045,
      invoice: "inv0456.pdf",
      progress: 75,
      status: "Paid",
    },
    {
      id: "#450",
      title: "Project Financial",
      hours: 60,
      totalHours: 100,
      cost: 1045,
      invoice: "inv0450.pdf",
      progress: 50,
      status: "Paid",
    },
  ];
  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>{translate("Quotes")}</CardTitle>
          <CardDescription>{translate("Manage and generate project quotes")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            {dummyQuotes?.map((quote: DictType, index: number) => (
              <Card key={index} className='overflow-hidden'>
                <div className='p-4'>
                  <div
                    className='flex justify-between items-center cursor-pointer'
                    onClick={() => toggleCard(index)}
                  >
                    <h2 className='text-xl font-semibold'>
                      {quote?.id} - {quote?.title}
                    </h2>
                    <Button variant='ghost' size='sm'>
                      <ChevronUp
                        className={`h-4 w-4 transition-transform ${expandedCards[index] ? "" : "rotate-180"}`}
                      />
                    </Button>
                  </div>

                  {expandedCards[index] && (
                    <div className='mt-4'>
                      <div className='grid grid-cols-12 rounded-t-md gap-2 p-2 bg-primary/20 text-xs font-medium'>
                        <div className='col-span-1'>{translate("ID")}</div>
                        <div className='col-span-2'>{translate("Title")}</div>
                        <div className='col-span-2'>
                          {translate("Hours")}/{translate("Costs")}
                        </div>
                        <div className='col-span-1'>{translate("Invoice")}</div>
                        <div className='col-span-3'>
                          {translate("Hours")}/{translate("Costs")}
                        </div>
                        <div className='col-span-1'>{translate("Status")}</div>
                        <div className='col-span-2'></div>
                      </div>
                      <QuoteRow quote={quote} revalidate={refetch} />
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {isLoading && <PmsLoader />}
          {dummyQuotes.length < 1 && !isLoading && (
            <div className='w-full flex items-center justify-center py-4 text-sm font-medium'>
              <p>{translate("No items found")}</p>
            </div>
          )}
        </CardContent>
        <CardFooter className='flex justify-between'>
          <div className='text-sm text-muted-foreground'>
            {translate("Showing")} {dummyQuotes?.length} {translate("of")} {dummyQuotes?.length}{" "}
            {translate("items")}
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ProjectQuoteManagerTab;
